/*
 * Thumbnail navigation for the "spotlight" product media layout.
 *
 * Pairs each thumb to its slide by data-media-id and scrolls the main slider on click.
 * The active thumb is driven by an IntersectionObserver watching the slides rather than
 * by the click itself, so it stays correct however the slide changed — arrow, swipe,
 * thumb click, or a variant swap re-rendering the gallery.
 *
 * Deliberately independent of the ProductSlider class in product.js: this only touches
 * the DOM and scroll position, so load order between the two files does not matter.
 */
if (!customElements.get('product-thumbnails')) {
  class ProductThumbnails extends HTMLElement {
    connectedCallback() {
      this.slider = document.getElementById(this.dataset.slider);
      if (!this.slider) return;

      this.track = this.querySelector('.product-thumbnails__track');
      this.thumbs = Array.from(this.querySelectorAll('.product-thumbnails__item'));

      // A single-media product has nothing to navigate between.
      if (this.thumbs.length < 2) {
        this.hidden = true;
        return;
      }

      this.visibility = new Map();

      this.thumbs.forEach((thumb) => {
        const activate = () => this.scrollToMedia(thumb.dataset.mediaId);
        // Hover switches the main image to the hovered thumbnail (desktop only —
        // thumbnails are hidden on mobile, where there's no hover anyway).
        thumb.addEventListener('mouseenter', activate);
        // Click still works for touch devices and keyboard activation.
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          activate();
        });
      });

      // Scoped to the slide elements: inner media wrappers carry their own
      // data-media-id with a different value and must not be observed.
      this.slides = Array.from(this.slider.querySelectorAll('.product-images__slide[data-media-id]'));
      this.slideByMediaId = new Map(this.slides.map((slide) => [slide.dataset.mediaId, slide]));
      this.activeMediaId = null;

      this.observer = new IntersectionObserver(this.onSlideVisibilityChange.bind(this), {
        root: this.slider,
        threshold: [0, 0.25, 0.5, 0.75, 1]
      });
      this.slides.forEach((slide) => this.observer.observe(slide));

      // Pagination dots — shown on mobile in place of the counter. Built from the
      // slides so the count always matches. Container is the empty .product-dots in
      // the stage (next to the slider); CSS hides it on desktop. Dots are clickable
      // like thumbnails.
      this.dotsContainer =
        this.slider.parentElement && this.slider.parentElement.querySelector('.product-dots');
      if (this.dotsContainer) {
        this.dots = this.slides.map((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product-dots__dot' + (index === 0 ? ' is-active' : '');
          dot.dataset.mediaId = slide.dataset.mediaId;
          dot.setAttribute('aria-label', 'Go to image ' + (index + 1));
          dot.addEventListener('click', () => this.scrollToMedia(slide.dataset.mediaId));
          this.dotsContainer.appendChild(dot);
          return dot;
        });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    onSlideVisibilityChange(entries) {
      entries.forEach((entry) => this.visibility.set(entry.target, entry.intersectionRatio));

      let mostVisible = null;
      let highestRatio = 0;

      this.visibility.forEach((ratio, slide) => {
        if (ratio > highestRatio) {
          highestRatio = ratio;
          mostVisible = slide;
        }
      });

      // Mid-swipe two slides are part-visible; wait until one clearly wins.
      if (mostVisible && highestRatio > 0.5) {
        this.setActive(mostVisible.dataset.mediaId);
      }
    }

    setActive(mediaId) {
      if (mediaId === this.activeMediaId) return;
      this.activeMediaId = mediaId;

      this.thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.mediaId === mediaId;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        if (isActive) this.revealThumb(thumb);
      });

      if (this.dots) {
        this.dots.forEach((dot) =>
          dot.classList.toggle('is-active', dot.dataset.mediaId === mediaId)
        );
      }

      this.updateActiveVideo(mediaId);
    }

    // Play the video in the slide that just became the main image; pause and rewind
    // any others so they show their poster again. No-op on products with no video.
    updateActiveVideo(mediaId) {
      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;

        if (slide.dataset.mediaId === mediaId) {
          // On small screens force the 480p rendition — browsers are
          // inconsistent about source media-attribute selection and the HLS
          // playlist leads with its 1080p variant, either of which can stall
          // playback on mobile data.
          const mobileSrc = video.dataset.mobileSrc;
          if (
            mobileSrc &&
            window.matchMedia('(max-width: 767px)').matches &&
            video.currentSrc.indexOf('SD-480p') === -1
          ) {
            video.src = mobileSrc;
            video.load();
          }
          // Muted + inline is what lets browsers autoplay without a user gesture.
          video.muted = true;
          video.playsInline = true;
          video.setAttribute('playsinline', '');
          const played = video.play();
          // iOS Low Power Mode (and some data-saver settings) rejects
          // programmatic autoplay. Surface native controls so the video is
          // never an unplayable frozen poster, then retry on the shopper's
          // next touch — playback started inside a real gesture is allowed
          // even in Low Power Mode.
          if (played && played.catch) {
            played.catch(() => {
              video.setAttribute('controls', '');
              const retry = () => {
                if (this.activeMediaId !== slide.dataset.mediaId) return;
                const attempt = video.play();
                if (attempt && attempt.then) {
                  attempt
                    .then(() => video.removeAttribute('controls'))
                    .catch(() => {});
                }
              };
              document.addEventListener('touchend', retry, { once: true, passive: true });
            });
          }
        } else if (!video.paused) {
          // Pause only — no rewind, so sliding back resumes where it left off.
          video.pause();
        }
      });
    }

    revealThumb(thumb) {
      if (!this.track) return;

      const trackBounds = this.track.getBoundingClientRect();
      const thumbBounds = thumb.getBoundingClientRect();

      // The thumbnail column scrolls vertically. Keep part of the neighbouring thumb
      // in view so it reads as scrollable, and so selecting a thumb near an edge
      // slides the column to reveal the next one.
      const peek = thumbBounds.height * 0.6;

      let delta = 0;
      if (thumbBounds.bottom + peek > trackBounds.bottom) {
        delta = thumbBounds.bottom + peek - trackBounds.bottom; // scroll down, reveal following thumbs
      } else if (thumbBounds.top - peek < trackBounds.top) {
        delta = thumbBounds.top - peek - trackBounds.top; // scroll up, reveal preceding thumbs
      } else {
        return; // comfortably in view already
      }

      // Scroll the column itself rather than scrollIntoView, which would also move
      // the page. Clamp so we never overscroll past the ends.
      const max = this.track.scrollHeight - this.track.clientHeight;
      const target = Math.max(0, Math.min(this.track.scrollTop + delta, max));
      this.track.scrollTo({ top: target, behavior: 'smooth' });
    }

    scrollToMedia(mediaId) {
      const slide = this.slideByMediaId.get(mediaId);
      if (!slide) return;

      const delta = slide.getBoundingClientRect().left - this.slider.getBoundingClientRect().left;
      // 'instant' (not 'smooth') so clicking a thumbnail jumps straight to the image
      // with no slide animation. Overrides the stage's CSS scroll-behavior: smooth.
      this.slider.scrollTo({ left: this.slider.scrollLeft + delta, behavior: 'instant' });
    }
  }

  customElements.define('product-thumbnails', ProductThumbnails);
}

// On mobile, swap every gallery video down to its 480p rendition at page
// load — before any play path (autoplay, native controls, slide change)
// can start fetching the heavy 1080p/HLS default.
(function () {
  function downshiftGalleryVideos() {
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    document.querySelectorAll('video[data-mobile-src]').forEach(function (video) {
      if (video.getAttribute('src') === video.dataset.mobileSrc) return;
      var wasPlaying = !video.paused;
      video.src = video.dataset.mobileSrc;
      video.load();
      if (wasPlaying) {
        var played = video.play();
        if (played && played.catch) played.catch(function () {});
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', downshiftGalleryVideos);
  } else {
    downshiftGalleryVideos();
  }
})();
